import React, { useState, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { PermitLetter, LetterGeneration, Car } from '../../types';
import Select from '../Form/Select';
import TextArea from '../Form/TextArea';
import Input from '../Form/Input';
import Button from '../Form/Button';
import { useBranches } from '../../hooks/useBranches';
import { generateCarPreviewHTML, generateCarTableHTML, buildCarPrintHTML } from './CarLetterTemplate';
import LetterPreviewPanel from './LetterPreviewPanel';

interface CarLetterGeneratorProps {
  letters: PermitLetter[];
  cars: Car[];
  onSubmit: (data: Partial<LetterGeneration>) => void;
  onCancel: () => void;
}

const CarLetterGenerator: React.FC<CarLetterGeneratorProps> = ({ 
  letters, cars, onSubmit, onCancel 
}) => {
  const { data: apiBranches = [] } = useBranches({ populate: '*' });
  const [selectedCars, setSelectedCars] = useState<string[]>([]);
  const [previewContent, setPreviewContent] = useState('');
  const [isPreviewEditable, setIsPreviewEditable] = useState(false);
  const [editableContent, setEditableContent] = useState('');

  const {
    register, handleSubmit,
    formState: { errors, isSubmitting },
    watch
  } = useForm<Partial<LetterGeneration>>({
    defaultValues: { letterId: '', customContent: '', recipients: [''], notes: '', branchId: '' }
  });

  const selectedLetterId = watch('letterId');
  const customContent = watch('customContent');
  const selectedBranchId = watch('branchId');

  const letterOptions = letters.map(letter => ({
    value: letter.id,
    label: `${letter.templateName} (${letter.language === 'en' ? 'English' : 'عربي'})`
  }));
  const shipBranches = (apiBranches as any[]).filter((branch: any) => branch.type === 'vessel');
  const branchOptions = shipBranches.map((branch: any) => ({
    value: branch.id?.toString() || branch.documentId || '', label: branch.name
  }));

  const selectedLetter = useMemo(
    () => letters.find(letter => String(letter.id) === String(selectedLetterId)),
    [letters, selectedLetterId]
  );
  const selectedBranch = useMemo(() => (apiBranches as any[]).find((b: any) => 
    b.id?.toString() === selectedBranchId || b.documentId === selectedBranchId
  ), [apiBranches, selectedBranchId]);

  const carData = useMemo(() => {
    return selectedCars.map(carId => {
      const car = cars.find(c => c.id === carId);
      if (!car) return null;
      return { plateNumber: car.plateNumber || '', chassisNumber: car.chassisNumber || '', engineNumber: car.engineNumber || '', manufacturingYear: car.manufacturingYear || '' };
    }).filter(Boolean) as { plateNumber: string; chassisNumber: string; engineNumber: string; manufacturingYear: string }[];
  }, [selectedCars, cars]);

  const branchName = selectedBranch?.name || '............';

  // Letter language comes from the selected template (English or Arabic layout).
  const letterLanguage: 'ar' | 'en' = selectedLetter?.language === 'en' ? 'en' : 'ar';

  const generateArabicLetterHTML = useMemo(() => {
    if (carData.length === 0) return '';
    return generateCarPreviewHTML(carData, branchName, letterLanguage);
  }, [carData, branchName, letterLanguage]);

  // The {{CAR_DETAILS}} placeholder means the cars table only.
  const detailsTableHTML = useMemo(() => {
    if (carData.length === 0) return '';
    return generateCarTableHTML(carData, letterLanguage);
  }, [carData, letterLanguage]);

  // Content precedence: what the user typed in this modal > the selected
  // template's saved content > the built-in default layout.
  const baseContent = customContent || selectedLetter?.content || '';

  React.useEffect(() => {
    if (selectedLetter && selectedCars.length > 0) {
      if (baseContent) {
        let content = baseContent;
        if (/\{\{CAR_DETAILS\}\}/.test(content)) {
          content = content.replace(/\{\{CAR_DETAILS\}\}/g, detailsTableHTML);
        } else {
          // Template forgot the placeholder — the selected cars must still
          // appear, so the table is appended after the letter text.
          content += detailsTableHTML;
        }
        content = content.replace(/\{\{DATE\}\}/g, new Date().toLocaleDateString(letterLanguage === 'en' ? 'en-GB' : 'ar-EG'));
        content = content.replace(/\{\{COMPANY_NAME\}\}/g, letterLanguage === 'en' ? 'PetroMarine Offshore Projects Support Co.' : 'شركة بترومارين لدعم المشاريع البحرية');
        content = content.replace(/\{\{UNIT_NAME\}\}/g, branchName);
        setPreviewContent(content);
      } else {
        setPreviewContent(generateArabicLetterHTML);
      }
      if (!isPreviewEditable) setEditableContent(baseContent || generateArabicLetterHTML);
    } else {
      setPreviewContent('');
      setEditableContent('');
    }
  }, [selectedLetter, selectedCars, generateArabicLetterHTML, detailsTableHTML, baseContent, isPreviewEditable, letterLanguage, branchName]);

  const letterLayout = {
    logoUrl: selectedLetter?.logoUrl,
    logoPosition: selectedLetter?.logoPosition,
    logoSize: selectedLetter?.logoSize,
    headerHtml: selectedLetter?.headerHtml,
    footerHtml: selectedLetter?.footerHtml,
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      const content = isPreviewEditable ? editableContent : previewContent;
      printWindow.document.write(buildCarPrintHTML(carData, branchName, letterLanguage, letterLayout, content));
      printWindow.document.close();
      printWindow.print();
    }
  };

  const handleCarToggle = (carId: string) => {
    setSelectedCars(prev => prev.includes(carId) ? prev.filter(id => id !== carId) : [...prev, carId]);
  };

  const handleSelectAllCars = () => {
    setSelectedCars(prev => prev.length === cars.length ? [] : cars.map(c => c.id));
  };

  const onSubmitForm = (data: Partial<LetterGeneration>) => {
    onSubmit({
      ...data,
      selectedItems: selectedCars,
      recipients: data.recipients?.filter(email => email.trim() !== '') || [],
      printHtml: buildCarPrintHTML(carData, branchName, letterLanguage, letterLayout, isPreviewEditable ? editableContent : previewContent),
    });
  };

  const emptyIcon = (
    <svg className="w-12 h-12 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2" />
    </svg>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
          <h2 className="text-xl font-semibold text-gray-900">Generate Car Letter</h2>
        </div>
        <form onSubmit={handleSubmit(onSubmitForm)} className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="space-y-6">
              <Select label="Letter Template" {...register('letterId', { required: 'Please select a letter template' })}
                options={letterOptions} error={errors.letterId?.message} placeholder="Select letter template..." />
              <Select label="الوحدة (Unit/Branch)" {...register('branchId', { required: 'Please select a branch' })}
                options={branchOptions} error={errors.branchId?.message} placeholder="Select unit/branch..." />
              <div>
                <div className="flex justify-between items-center mb-3">
                  <label className="block text-sm font-medium text-gray-700">Select Cars ({selectedCars.length} selected)</label>
                  <button type="button" onClick={handleSelectAllCars} className="text-sm text-primary-600 hover:text-primary-700">
                    {selectedCars.length === cars.length ? 'Deselect All' : 'Select All'}
                  </button>
                </div>
                <div className="max-h-48 overflow-y-auto border border-gray-300 rounded-md p-3 space-y-2">
                  {cars.map(car => (
                    <label key={car.id} className="flex items-center space-x-2">
                      <input type="checkbox" checked={selectedCars.includes(car.id)} onChange={() => handleCarToggle(car.id)}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded" />
                      <span className="text-sm text-gray-900">{car.code} - {car.ownerName} ({car.plateNumber})</span>
                    </label>
                  ))}
                </div>
                {selectedCars.length === 0 && <p className="text-sm text-red-600 mt-1">Please select at least one car</p>}
              </div>
              <div>
                <TextArea label="Custom Content (Optional)" {...register('customContent')}
                  placeholder="Leave empty to use Arabic template, or customize the letter content here..." rows={6} />
                <p className="text-sm text-gray-500 mt-1">If provided, this will override the template content</p>
              </div>
              <Input label="Recipients (Email)" {...register('recipients.0')} placeholder="Enter recipient email address" type="email" />
              <TextArea label="Notes (Optional)" {...register('notes')} placeholder="Additional notes about this letter generation..." rows={3} />
            </div>
            <LetterPreviewPanel
              showPreview={!!selectedLetter && selectedCars.length > 0}
              isEditable={isPreviewEditable}
              previewContent={previewContent}
              editableContent={editableContent}
              onEditableChange={setEditableContent}
              onEdit={() => { setIsPreviewEditable(true); setEditableContent(previewContent); }}
              onSave={() => { setIsPreviewEditable(false); setPreviewContent(editableContent); }}
              onCancel={() => { setIsPreviewEditable(false); setEditableContent(previewContent); }}
              onPrint={handlePrint}
              emptyIcon={emptyIcon}
              emptyMessage="Select a template and cars to preview the letter"
              layout={letterLayout}
            />
          </div>
          <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200 mt-8">
            <Button type="button" variant="secondary" onClick={onCancel} disabled={isSubmitting}>Cancel</Button>
            <Button type="submit" variant="primary" disabled={isSubmitting || selectedCars.length === 0 || !selectedLetterId} loading={isSubmitting}>
              Generate Letter
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CarLetterGenerator;
